import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { reportsApi, clientsApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ArrowLeft, Plus, FileText, Filter, Search, CalendarIcon, Download, X, Package, Loader2, Save, Mail, Image as ImageIcon, Users, List, FilePlus, Archive } from 'lucide-react';
import { EmailReportDialog } from '../components/EmailReportDialog';
import { SavedBundlesDialog } from '../components/SavedBundlesDialog';
import { LogoHome } from '../components/LogoHome';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e3b3c70d-c72e-49dc-b41a-da6affdc6e46/artifacts/4w4rfom9_IMG_0777.PNG";

export default function ReportsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [clients, setClients] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedReports, setSelectedReports] = useState([]);
  const [showBulkEmail, setShowBulkEmail] = useState(false);
  const [bulkExporting, setBulkExporting] = useState(false);
  const [savingPdf, setSavingPdf] = useState(null);
  const [groupByClient, setGroupByClient] = useState(false);
  const [consolidatingClientId, setConsolidatingClientId] = useState(null);
  const [includeCoverPage, setIncludeCoverPage] = useState(true);
  const [saveToArchive, setSaveToArchive] = useState(true);
  const [unifiedMode, setUnifiedMode] = useState(false);
  const [includeExecSummary, setIncludeExecSummary] = useState(true);
  const [showBundlesDialog, setShowBundlesDialog] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [reportsRes, clientsRes] = await Promise.all([
        reportsApi.getAll(),
        clientsApi.getAll()
      ]);
      setReports(reportsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterType !== 'all') params.template_type = filterType;
      if (filterClient !== 'all') params.client_id = filterClient;
      if (searchQuery) params.search = searchQuery;
      if (dateFrom) params.date_from = format(dateFrom, 'yyyy-MM-dd');
      if (dateTo) params.date_to = format(dateTo, 'yyyy-MM-dd');
      
      const response = await reportsApi.getAll(params);
      setReports(response.data);
    } catch (error) {
      toast.error('Failed to search reports');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilterStatus('all');
    setFilterType('all');
    setFilterClient('all');
    setSearchQuery('');
    setDateFrom(null);
    setDateTo(null);
    loadData();
  };

  const handleDownloadPdf = async (reportId, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const blob = await reportsApi.getPdfBlob(reportId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${reportId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const toggleReportSelection = (reportId, e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedReports(prev => 
      prev.includes(reportId) 
        ? prev.filter(id => id !== reportId)
        : [...prev, reportId]
    );
  };

  const selectAllReports = () => {
    const exportableIds = filteredReports.filter(r => r.status !== 'draft').map(r => r.id);
    setSelectedReports(prev => prev.length === exportableIds.length ? [] : exportableIds);
  };

  const handleBulkExport = async () => {
    if (selectedReports.length === 0) {
      toast.error('Please select at least one report to export');
      return;
    }
    
    setBulkExporting(true);
    try {
      const blob = await reportsApi.bulkExportPdf(selectedReports);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `visitex_reports_${new Date().toISOString().slice(0,10)}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`${selectedReports.length} reports exported successfully`);
      setSelectedReports([]);
    } catch (error) {
      toast.error('Failed to export reports');
    } finally {
      setBulkExporting(false);
    }
  };

  const handleSavePdf = async (reportId, e) => {
    e.preventDefault();
    e.stopPropagation();
    setSavingPdf(reportId);
    try {
      await reportsApi.savePdf(reportId);
      toast.success('PDF saved permanently');
    } catch (error) {
      toast.error('Failed to save PDF');
    } finally {
      setSavingPdf(null);
    }
  };

  const handleConsolidateClient = async (clientId, clientName, reportIds) => {
    if (!reportIds || reportIds.length === 0) {
      toast.error('No exportable reports for this client');
      return;
    }
    setConsolidatingClientId(clientId);
    try {
      const blob = unifiedMode
        ? await reportsApi.unifiedPdf(reportIds, saveToArchive, null, includeExecSummary)
        : await reportsApi.consolidatedPdf(reportIds, includeCoverPage, saveToArchive);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (clientName || 'reports').replace(/[^a-z0-9]+/gi, '_');
      a.download = `${safeName}_${unifiedMode ? 'unified' : 'consolidated'}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Consolidated ${reportIds.length} report${reportIds.length === 1 ? '' : 's'} into one PDF${saveToArchive ? ' (saved to archive)' : ''}`);
    } catch (error) {
      toast.error('Failed to consolidate reports');
    } finally {
      setConsolidatingClientId(null);
    }
  };

  const filteredReports = reports.filter(report => {
    if (filterStatus !== 'all' && report.status !== filterStatus) return false;
    if (filterType !== 'all' && report.template_type !== filterType) return false;
    if (filterClient !== 'all' && report.client_id !== filterClient) return false;
    return true;
  });

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || 'Unknown';
  };

  const hasActiveFilters = filterStatus !== 'all' || filterType !== 'all' || filterClient !== 'all' || searchQuery || dateFrom || dateTo;
  const exportableReports = filteredReports.filter(r => r.status !== 'draft');

  // Group filtered reports by client (used in "Group by Client" view)
  const groupedReports = (() => {
    const map = new Map();
    filteredReports.forEach(r => {
      const cid = r.client_id || 'unknown';
      if (!map.has(cid)) {
        map.set(cid, { clientId: cid, clientName: getClientName(cid), reports: [] });
      }
      map.get(cid).reports.push(r);
    });
    // Sort each group's reports newest-first; then sort groups by client name asc
    return Array.from(map.values())
      .map(g => {
        const sortedReports = [...g.reports].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        const exportableIds = sortedReports.filter(r => r.status !== 'draft').map(r => r.id);
        const selectedInGroup = exportableIds.filter(id => selectedReports.includes(id));
        // If user picked specific reports in this group, use them; otherwise consolidate all
        const consolidateIds = selectedInGroup.length > 0 ? selectedInGroup : exportableIds;
        return { ...g, reports: sortedReports, exportableIds, selectedInGroup, consolidateIds };
      })
      .sort((a, b) => a.clientName.localeCompare(b.clientName));
  })();

  const toggleGroupSelection = (group) => {
    const allSelected = group.exportableIds.length > 0 && group.selectedInGroup.length === group.exportableIds.length;
    setSelectedReports(prev => {
      const others = prev.filter(id => !group.exportableIds.includes(id));
      return allSelected ? others : [...others, ...group.exportableIds];
    });
  };

  const renderReportCard = (report) => (
    <Link to={`/reports/${report.id}`} key={report.id}>
      <Card className="card-hover">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-4 flex-1">
              {report.status !== 'draft' && (
                <Checkbox
                  checked={selectedReports.includes(report.id)}
                  onCheckedChange={() => {}}
                  onClick={(e) => toggleReportSelection(report.id, e)}
                  data-testid={`select-report-${report.id}`}
                />
              )}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-heading font-semibold text-slate-900 capitalize">
                    {report.template_type.replace(/_/g, ' ')}
                  </h3>
                  <Badge
                    variant={report.status === 'submitted' ? 'default' : 'secondary'}
                    className={report.status === 'submitted' ? 'bg-green-100 text-green-700' :
                      report.status === 'generated' ? 'bg-blue-100 text-blue-700' :
                      report.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                      report.status === 'changes_requested' ? 'bg-amber-100 text-amber-700' : ''}
                  >
                    {report.status === 'changes_requested' ? 'Changes Requested' : report.status}
                  </Badge>
                  {report.version > 1 && (
                    <Badge variant="outline" className="text-xs">
                      v{report.version}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-slate-500">
                  {!groupByClient && <>Client: {getClientName(report.client_id)} • </>}
                  {new Date(report.created_at).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney', day: 'numeric', month: 'short', year: 'numeric' })}
                  {(() => {
                    const photoCount = Object.values(report.form_data || {})
                      .flatMap(v => Array.isArray(v) ? v : [])
                      .filter(x => x && typeof x === 'object' && x.id && (x.filename || x.size != null)).length;
                    return photoCount > 0 ? (
                      <span className="ml-2 inline-flex items-center gap-1 text-teal-700">
                        <ImageIcon className="h-3.5 w-3.5" />
                        {photoCount} photo{photoCount !== 1 ? 's' : ''}
                      </span>
                    ) : null;
                  })()}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {report.compliance_score && (
                <div className="w-32">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-500">Compliance</span>
                    <span className="font-medium">{report.compliance_score}%</span>
                  </div>
                  <Progress value={report.compliance_score} className="h-2" />
                </div>
              )}
              {report.status !== 'draft' && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => handleSavePdf(report.id, e)}
                    disabled={savingPdf === report.id}
                    data-testid={`save-pdf-${report.id}`}
                  >
                    <Save className="h-4 w-4 mr-1" />
                    {savingPdf === report.id ? '...' : 'Save'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => handleDownloadPdf(report.id, e)}
                    data-testid={`download-pdf-${report.id}`}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    PDF
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="reports-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-slate-200" />
              <h1 className="font-heading font-semibold text-slate-900">Reports</h1>
            </div>
            <LogoHome className="h-12 w-auto" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="font-heading text-2xl font-bold text-slate-900">Report Archive</h2>
            <p className="text-slate-500">{filteredReports.length} reports found</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowBundlesDialog(true)}
              className="border-teal-200 text-teal-700 hover:bg-teal-50"
              data-testid="open-saved-bundles-btn"
            >
              <Archive className="h-4 w-4 mr-2" />
              Saved Bundles
            </Button>
            {selectedReports.length > 0 && (
              <>
                <Button 
                  onClick={handleBulkExport}
                  disabled={bulkExporting}
                  variant="outline"
                  className="border-teal-200 text-teal-700 hover:bg-teal-50"
                  data-testid="bulk-export-btn"
                >
                  {bulkExporting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Package className="h-4 w-4 mr-2" />
                  )}
                  Export {selectedReports.length} Reports
                </Button>
                <Button
                  onClick={() => setShowBulkEmail(true)}
                  variant="outline"
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  data-testid="bulk-email-btn"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Email {selectedReports.length} PDF{selectedReports.length === 1 ? '' : 's'}
                </Button>
              </>
            )}
            <Link to="/reports/new">
              <Button className="bg-teal-600 hover:bg-teal-700" data-testid="new-report-link">
                <Plus className="h-4 w-4 mr-2" />
                New Report
              </Button>
            </Link>
          </div>
        </div>

        {/* Search & Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            {/* Search Bar */}
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search reports by content..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10 h-12"
                  data-testid="search-input"
                />
              </div>
              <Button onClick={handleSearch} className="bg-teal-600 hover:bg-teal-700 h-12 px-6">
                <Search className="h-4 w-4 mr-2" />
                Search
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowFilters(!showFilters)}
                className={`h-12 ${showFilters ? 'bg-slate-100' : ''}`}
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
                {hasActiveFilters && (
                  <Badge className="ml-2 bg-teal-600">Active</Badge>
                )}
              </Button>
            </div>

            {/* Filter Options */}
            {showFilters && (
              <div className="pt-4 border-t border-slate-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Status</label>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger data-testid="filter-status">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="generated">Generated</SelectItem>
                        <SelectItem value="submitted">Submitted</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="changes_requested">Changes Requested</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Type</label>
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger data-testid="filter-type">
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="progress_note">Progress Note</SelectItem>
                        <SelectItem value="incident_form">Incident Form</SelectItem>
                        <SelectItem value="allied_health_summary">Allied Health Summary</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Client</label>
                    <Select value={filterClient} onValueChange={setFilterClient}>
                      <SelectTrigger data-testid="filter-client">
                        <SelectValue placeholder="Client" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Clients</SelectItem>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">From Date</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal h-10">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {dateFrom ? format(dateFrom, 'PPP') : 'Pick date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">To Date</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal h-10">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {dateTo ? format(dateTo, 'PPP') : 'Pick date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar mode="single" selected={dateTo} onSelect={setDateTo} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                {hasActiveFilters && (
                  <div className="mt-4 flex justify-end">
                    <Button variant="ghost" onClick={clearFilters} className="text-slate-600">
                      <X className="h-4 w-4 mr-2" />
                      Clear Filters
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reports List */}
        {filteredReports.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">No reports found. Try adjusting your filters or create a new report.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* View Toggle + Select All */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-slate-100 rounded-lg">
              {exportableReports.length > 0 && !groupByClient && (
                <div className="flex items-center gap-3 flex-1">
                  <Checkbox
                    checked={selectedReports.length === exportableReports.length && exportableReports.length > 0}
                    onCheckedChange={selectAllReports}
                    data-testid="select-all-reports"
                  />
                  <span className="text-sm text-slate-600">
                    Select all {exportableReports.length} exportable reports
                  </span>
                </div>
              )}
              <div className={`flex items-center gap-2 ${!groupByClient && exportableReports.length > 0 ? '' : 'flex-1 justify-end sm:justify-start'} sm:ml-auto`}>
                {groupByClient && (
                  <>
                    <label className="flex items-center gap-2 text-sm text-slate-600 mr-2 cursor-pointer" data-testid="unified-mode-label">
                      <Checkbox
                        checked={unifiedMode}
                        onCheckedChange={(v) => setUnifiedMode(Boolean(v))}
                        data-testid="unified-mode-toggle"
                      />
                      Unified summary
                    </label>
                    {unifiedMode && (
                      <label className="flex items-center gap-2 text-sm text-slate-600 mr-2 cursor-pointer" data-testid="exec-summary-label">
                        <Checkbox
                          checked={includeExecSummary}
                          onCheckedChange={(v) => setIncludeExecSummary(Boolean(v))}
                          data-testid="exec-summary-toggle"
                        />
                        AI Executive Summary
                      </label>
                    )}
                    {!unifiedMode && (
                      <label className="flex items-center gap-2 text-sm text-slate-600 mr-2 cursor-pointer" data-testid="cover-toggle-label">
                        <Checkbox
                          checked={includeCoverPage}
                          onCheckedChange={(v) => setIncludeCoverPage(Boolean(v))}
                          data-testid="cover-toggle"
                        />
                        Include cover page
                      </label>
                    )}
                    <label className="flex items-center gap-2 text-sm text-slate-600 mr-2 cursor-pointer" data-testid="save-archive-label">
                      <Checkbox
                        checked={saveToArchive}
                        onCheckedChange={(v) => setSaveToArchive(Boolean(v))}
                        data-testid="save-archive-toggle"
                      />
                      Save to archive
                    </label>
                  </>
                )}
                <span className="text-sm text-slate-500 mr-1">View:</span>
                <Button
                  variant={groupByClient ? 'outline' : 'default'}
                  size="sm"
                  className={groupByClient ? '' : 'bg-teal-600 hover:bg-teal-700'}
                  onClick={() => setGroupByClient(false)}
                  data-testid="view-flat-btn"
                >
                  <List className="h-4 w-4 mr-1" />
                  All Reports
                </Button>
                <Button
                  variant={groupByClient ? 'default' : 'outline'}
                  size="sm"
                  className={groupByClient ? 'bg-teal-600 hover:bg-teal-700' : ''}
                  onClick={() => setGroupByClient(true)}
                  data-testid="view-grouped-btn"
                >
                  <Users className="h-4 w-4 mr-1" />
                  Group by Client
                </Button>
              </div>
            </div>

            {!groupByClient && filteredReports.map(renderReportCard)}

            {groupByClient && groupedReports.map(group => (
              <Card key={group.clientId} className="overflow-hidden" data-testid={`client-group-${group.clientId}`}>
                <CardHeader className="bg-gradient-to-r from-teal-50 to-emerald-50 border-b border-teal-100 py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-start gap-3">
                      {group.exportableIds.length > 0 && (
                        <Checkbox
                          className="mt-1"
                          checked={group.selectedInGroup.length === group.exportableIds.length && group.exportableIds.length > 0}
                          onCheckedChange={() => toggleGroupSelection(group)}
                          data-testid={`select-all-group-${group.clientId}`}
                        />
                      )}
                      <div>
                        <CardTitle className="font-heading text-lg text-slate-900 flex items-center gap-2">
                          <Users className="h-5 w-5 text-teal-600" />
                          {group.clientName}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          {group.selectedInGroup.length > 0 ? (
                            <span className="text-teal-700 font-medium">
                              {group.selectedInGroup.length} of {group.exportableIds.length} selected
                            </span>
                          ) : (
                            <>
                              {group.reports.length} report{group.reports.length === 1 ? '' : 's'}
                              {group.exportableIds.length !== group.reports.length && (
                                <span className="ml-1 text-slate-400">
                                  ({group.exportableIds.length} exportable)
                                </span>
                              )}
                            </>
                          )}
                        </CardDescription>
                      </div>
                    </div>
                    <Button
                      onClick={() => handleConsolidateClient(group.clientId, group.clientName, group.consolidateIds)}
                      disabled={consolidatingClientId === group.clientId}
                      className={`bg-teal-600 hover:bg-teal-700 ${group.consolidateIds.length === 0 ? 'hidden' : ''}`}
                      size="sm"
                      data-testid={`consolidate-btn-${group.clientId}`}
                    >
                      {consolidatingClientId === group.clientId ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <FilePlus className="h-4 w-4 mr-2" />
                      )}
                      {group.selectedInGroup.length > 0
                        ? `${unifiedMode ? 'Unify' : 'Consolidate'} ${group.consolidateIds.length} selected into 1 PDF`
                        : `${unifiedMode ? 'Unify' : 'Consolidate'} all ${group.consolidateIds.length} into 1 PDF`}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-4 space-y-3 bg-slate-50/50">
                  {group.reports.map(renderReportCard)}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
      <EmailReportDialog
        open={showBulkEmail}
        onOpenChange={setShowBulkEmail}
        mode="bulk"
        reportIds={selectedReports}
      />
      <SavedBundlesDialog
        open={showBundlesDialog}
        onOpenChange={setShowBundlesDialog}
        canDelete={user?.role === 'organisation' || user?.role === 'supervisor'}
      />
    </div>
  );
}
